/**
 * CTD CONF-013 — what an Export button tells the workbook about itself.
 *
 * The downloaded file used to hold only the rows, so nothing in it said which report it was, how the
 * rows were chosen, or which computation of the statistics they came from. The API turns the entries
 * below into an "Export info" sheet and adds the facts only it knows (export time, account, row count).
 *
 * The data snapshot is read from the nearest report region that publishes one (`data-snapshot-at`, set
 * by the Item Summaries hub from the stats or reliability job). A panel outside that region reads live
 * data, and the sheet says so rather than leaving the question open.
 */

export type ExportInfo = Record<string, string | number>;

export const LIVE_DATA_NOTE = 'live data at export time (not a stored statistics snapshot)';

export type ExportScope = 'Selected rows' | 'Filtered rows' | 'All rows' | 'Panel list';

export interface SnapshotContext {
  snapshotAt: string;
  report: string;
  windowId: string;
}

/** The report region around the button, if any. `element` is whatever has `closest`, so a test can pass a stand-in. */
export const readSnapshotContext = (element: { closest?: (selector: string) => any } | null | undefined): SnapshotContext => {
  const region = element?.closest ? element.closest('[data-snapshot-at]') : null;
  const read = (name: string): string => (region?.getAttribute?.(name) || '').toString().trim();
  return { snapshotAt: read('data-snapshot-at'), report: read('data-report'), windowId: read('data-window-id') };
};

/** How the exported rows were chosen, from the same grid state `resolveExportRows` reads. */
export const describeExportScope = (source: {
  gridApi?: any;
  columnDefs?: any[] | null;
  records?: any[] | null;
  useSelection?: boolean;
}, rowsExported: number): ExportScope => {
  if (!Array.isArray(source.columnDefs)) return 'Panel list';
  const records = Array.isArray(source.records) ? source.records : [];
  let selected = 0;
  if (source.useSelection !== false && source.gridApi?.forEachNodeAfterFilterAndSort) {
    source.gridApi.forEachNodeAfterFilterAndSort((node: any) => { if (node?.data && node.isSelected?.()) selected++; });
  }
  if (selected > 0) return 'Selected rows';
  return rowsExported < records.length ? 'Filtered rows' : 'All rows';
};

const text = (value: unknown): string => (value === null || value === undefined ? '' : String(value)).trim();

export const buildExportInfo = (input: {
  reportName?: string;
  fileName?: string;
  scope: ExportScope;
  rowsAvailable: number | null;
  context: SnapshotContext;
}): ExportInfo => {
  const info: ExportInfo = {};
  const report = text(input.reportName) || text(input.fileName);
  if (report) info['Report'] = report;
  if (text(input.fileName)) info['File name'] = text(input.fileName);
  if (input.context.report) info['Report view'] = input.context.report;
  if (input.context.windowId) info['Scoring window id'] = input.context.windowId;
  info['Rows chosen by'] = input.scope;
  if (input.rowsAvailable !== null && Number.isFinite(input.rowsAvailable)) info['Rows available'] = input.rowsAvailable;
  info['Data snapshot (UTC)'] = input.context.snapshotAt || LIVE_DATA_NOTE;
  return info;
};
